/* eslint-disable max-len */
/* eslint-disable no-await-in-loop */
import { useTranslate } from '@centecomm/portal-service-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generateContentHash } from 'utils/generateContentHash';
import DynamicTranslationCacheService from '../Services/DynamicTranslationCache';
import { MainAppLanguage, SupportedLanguageCode } from '../Config/SupportedLanguages';
import config from '../Config/Env';

// Reference counting system for shared cache instance
let sharedCacheInstance: DynamicTranslationCacheService | null = null;
let activeInstanceCount = 0;

/**
 * Get or create a shared cache instance.
 * @returns The shared cache instance.
 */
const getOrCreateCacheInstance = (): DynamicTranslationCacheService => {
    if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
        console.log('[idt-i18n::useDynamicTranslation] Getting or creating dynamic translation cache instance');
    }

    if (!sharedCacheInstance) {
        if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
            console.log('[idt-i18n::useDynamicTranslation] Creating dynamic translation cache instance');
        }

        sharedCacheInstance = new DynamicTranslationCacheService({
            ttl: config.CACHES.DYNAMIC.EXPIRATION_TIME,
            maxMemoryItems: config.CACHES.DYNAMIC.MAX_MEMORY_ITEMS,
            storageKeyPrefix: config.CACHES.DYNAMIC.PREFIX,
            maxLocalStorageItems: config.CACHES.DYNAMIC.MAX_LOCAL_STORAGE_ITEMS,
            cacheVersion: config.CACHES.VERSION,
            loggingEnabled: config.LOGGING.DYNAMIC_TRANSLATION.ENABLED,
        });
    }

    return sharedCacheInstance;
};

/**
 * Cleanup the cache if no instances are using it.
 */
const cleanupCacheIfUnused = (): void => {
    if (activeInstanceCount === 0 && sharedCacheInstance) {
        if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
            console.log(
                '[idt-i18n::useDynamicTranslation] Clearing dynamic translation cache instance because no instances are using it',
            );
        }

        sharedCacheInstance.clear();
        sharedCacheInstance = null;
    }
};

/**
 * Options for the useDynamicTranslation hook.
 */
export interface UseDynamicTranslationOptions {
    /**
     * Whether to enable the cache.
     */
    enableCache?: boolean;

    /**
     * Whether to fallback to the original content if the translation fails.
     */
    fallbackToOriginal?: boolean;

    /**
     * Whether to bypass the main app language from the translation.
     */
    bypassMainAppLanguage?: boolean;

    /**
     * Whether to show a loading state.
     */
    showLoadingState?: boolean;

    /**
     * The number of retry attempts.
     */
    retryAttempts?: number;

    /**
     * Callback for when the translation starts.
     */
    onTranslationStart?: (text: string, targetLanguage: string) => void;

    /**
     * Callback for when the translation completes.
     */
    onTranslationComplete?: (text: string, translatedText: string, targetLanguage: string) => void;

    /**
     * Callback for when the translation fails.
     */
    onTranslationError?: (error: Error, text: string, targetLanguage: string) => void;
}

/**
 * Result of the useDynamicTranslation hook.
 */
interface UseDynamicTranslationResult {
    /**
     * The translated content.
     */
    translatedContent: string;

    /**
     * Whether the translation is loading.
     */
    isLoading: boolean;

    /**
     * The error if the translation fails.
     */
    error: Error | null;

    /**
     * Whether the translation has been performed.
     */
    hasTranslation: boolean;

    /**
     * Retry the translation.
     */
    retry: () => void;

    /**
     * The original content.
     */
    originalContent: string;
}

/**
 * Hook to translate content dynamically.
 * Internally uses the translateContent API from the service packages and caches the results.
 * @param content - The content to translate.
 * @param options - The options for the hook.
 * @returns The result of the translation.
 */
function useDynamicTranslation(content: string, options: UseDynamicTranslationOptions = {}): UseDynamicTranslationResult {
    const { i18n } = useTranslation();
    const { translateContent: translateContentApi } = useTranslate();
    const [translatedContent, setTranslatedContent] = useState<string>(content);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [hasTranslation, setHasTranslation] = useState<boolean>(false);

    const currentLanguage = i18n.language;
    const abortControllerRef = useRef<AbortController | null>(null);

    // Get shared cache instance and track this component's usage
    const componentRef = useRef<{ isTracked: boolean }>({ isTracked: false });
    const dynamicTranslationCache = getOrCreateCacheInstance();

    // Track this component's usage of the cache
    useEffect(() => {
        if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
            console.log('[idt-i18n::useDynamicTranslation] Tracking dynamic translation cache instance');
        }

        // Only increment if this component hasn't been tracked before
        if (!componentRef.current.isTracked) {
            activeInstanceCount += 1;
            componentRef.current.isTracked = true;
        }

        return () => {
            if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
                console.log('[idt-i18n::useDynamicTranslation] Untracking dynamic translation cache instance');
            }

            // Only decrement if this component was tracked
            if (componentRef.current.isTracked) {
                activeInstanceCount -= 1;
                componentRef.current.isTracked = false;
                cleanupCacheIfUnused();
            }
        };
    }, []);

    const {
        enableCache = true,
        bypassMainAppLanguage = false,
        fallbackToOriginal = true,
        showLoadingState = false,
        retryAttempts = 3,
        onTranslationStart,
        onTranslationComplete,
        onTranslationError,
    } = options;

    // Generate cache key for content
    const generateCacheKey = useCallback(async (text: string, language: SupportedLanguageCode) => {
        const contentHash = await generateContentHash(text);
        return `dynamic_${language}_${contentHash}`;
    }, []);

    const callTranslateApi = useCallback(
        async (text: string, targetLanguage: SupportedLanguageCode, signal: AbortSignal) => {
            if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
                console.log('[idt-i18n::useDynamicTranslation] Calling translate API for dynamic translation');
            }

            let lastError: Error | null = null;

            for (let attempt = 0; attempt <= retryAttempts; attempt += 1) {
                try {
                    return await translateContentApi(text, targetLanguage, signal);
                } catch (err) {
                    lastError = err;

                    if (err.name === 'AbortError' || attempt === retryAttempts) {
                        throw err;
                    }

                    if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
                        console.log(
                            '[idt-i18n::useDynamicTranslation] Error in translate API for dynamic translation - Retrying...',
                            err,
                        );
                    }

                    // Exponential backoff
                    const delay = Math.min(1000 * 2 ** attempt, 10000);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                }
            }

            throw lastError;
        },
        [],
    );

    // Main translation function
    const translateContent = useCallback(
        async (text: string, targetLanguage: SupportedLanguageCode) => {
            if (!text || text.trim() === '') return text;

            // Skip translation if target language is the default language
            // If the bypassMainAppLanguage flag is set, we will translate the content anyway (useful for the Conversations/Chat feature)
            // This will effectively translte
            if (targetLanguage === MainAppLanguage && !bypassMainAppLanguage) {
                return text;
            }

            onTranslationStart?.(text, targetLanguage);

            const cacheKey = await generateCacheKey(text, targetLanguage);

            // Check cache first
            if (enableCache) {
                const cachedTranslation = dynamicTranslationCache.get(cacheKey);
                if (cachedTranslation) {
                    if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
                        console.log(
                            '[idt-i18n::useDynamicTranslation] Returning cached translation for dynamic translation',
                        );
                    }

                    onTranslationComplete?.(text, cachedTranslation, targetLanguage);

                    return cachedTranslation;
                }
            }

            // Cancel any existing request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            try {
                if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
                    console.log('[idt-i18n::useDynamicTranslation] Starting translation for dynamic translation');
                }

                const translatedText = await callTranslateApi(text, targetLanguage, abortControllerRef.current.signal);

                // Cache the result
                if (enableCache && translatedText && translatedText.translation) {
                    dynamicTranslationCache.set(cacheKey, translatedText.translation);
                }

                onTranslationComplete?.(text, translatedText.translation, targetLanguage);

                return translatedText.translation;
            } catch (err) {
                if (err.name === 'AbortError') {
                    if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
                        console.log('[idt-i18n::useDynamicTranslation] Aborting translation for dynamic translation');
                    }

                    return text; // Return original if aborted
                }

                onTranslationError?.(err, text, targetLanguage);

                if (fallbackToOriginal) {
                    if (config.LOGGING.DYNAMIC_TRANSLATION.ENABLED) {
                        console.log(
                            '[idt-i18n::useDynamicTranslation] Falling back to original content for dynamic translation',
                        );
                    }

                    return text;
                }

                throw err;
            }
        },
        [
            enableCache,
            bypassMainAppLanguage,
            fallbackToOriginal,
            retryAttempts,
            generateCacheKey,
            onTranslationStart,
            onTranslationComplete,
            onTranslationError,
        ],
    );

    // Effect to handle translation when content or language changes
    useEffect(() => {
        const performTranslation = async () => {
            if (!content || content.trim() === '') {
                setTranslatedContent(content);
                setHasTranslation(false);
                return;
            }

            // Reset states
            setError(null);

            if (showLoadingState) {
                setIsLoading(true);
            }

            try {
                const translated = await translateContent(content, currentLanguage);
                setTranslatedContent(translated);
                setHasTranslation(translated !== content);
            } catch (err) {
                setError(err);
                setTranslatedContent(fallbackToOriginal ? content : '');
                setHasTranslation(false);
            } finally {
                setIsLoading(false);
            }
        };

        performTranslation();

        // Cleanup function
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [content, currentLanguage, translateContent, showLoadingState, fallbackToOriginal]);

    // Manual retry function
    const retry = useCallback(() => {
        if (content) {
            setError(null);
            setIsLoading(true);
            translateContent(content, currentLanguage)
                .then((translated) => {
                    setTranslatedContent(translated);
                    setHasTranslation(translated !== content);
                })
                .catch((err) => {
                    setError(err);
                    setTranslatedContent(fallbackToOriginal ? content : '');
                    setHasTranslation(false);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [content, currentLanguage, translateContent, fallbackToOriginal]);

    return {
        translatedContent,
        isLoading,
        error,
        hasTranslation,
        retry,
        originalContent: content,
    };
}

export default useDynamicTranslation;
